/**
 * Project → "Project Log / Analytics" table — the original's rules, kept apart from the markup:
 * - rows are the configured metric names, else every label found in the project's logs;
 * - "Total" (sum) only for a metric typed "That day only" whose values are not percentages;
 * - "Change" (last − first) for any other typed metric with 2+ numeric points;
 * - a metric with no type set shows neither.
 */
import type { MetricKind, Project, ProjectUpdate } from '@/types/models';
import { round2, sum } from '@/domain/format';
import { kindOf, numOf } from '@/domain/metrics';

export type MetricSummary =
  | { type: 'total'; total: number; days: number }
  | { type: 'change'; change: number; pct: boolean; from: string; to: string }
  | null;

export interface MetricRow {
  label: string;
  kind: MetricKind | '';
  /** One value per Project Log (oldest first); null when that log does not have the metric. */
  values: (string | null)[];
  summary: MetricSummary;
}

/** `updates` are the project's Project Logs, newest first. */
export function metricTable(p: Project, updates: ProjectUpdate[]): { dates: string[]; rows: MetricRow[] } {
  const keys = p.metricKeys.length ? p.metricKeys : [...new Set(updates.flatMap((u) => u.metrics.map((m) => m.label)))];
  const asc = [...updates].reverse();
  const rows = keys.map((label): MetricRow => {
    const kind = kindOf(p, label);
    const values = asc.map((u) => {
      const m = u.metrics.find((x) => x.label === label);
      return m ? m.value : null;
    });
    const pts = asc.map((u, i) => ({ d: u.date, n: numOf(values[i]), pct: /%/.test(values[i] || '') })).filter((x): x is { d: string; n: number; pct: boolean } => x.n !== null);
    const isPct = pts.some((x) => x.pct);
    let summary: MetricSummary = null;
    if (asc.length > 1 && pts.length) {
      if (kind === 'daily' && !isPct) summary = { type: 'total', total: sum(pts.map((x) => x.n)), days: pts.length };
      else if (kind && pts.length > 1) summary = { type: 'change', change: round2(pts[pts.length - 1].n - pts[0].n), pct: isPct, from: pts[0].d, to: pts[pts.length - 1].d };
    }
    return { label, kind, values, summary };
  });
  return { dates: asc.map((u) => u.date), rows };
}
